import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Calendar, ArrowLeft, Share2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlog = async () => {
      try {
        const response = await axios.get(`${API}/blogs/${slug}`);
        setBlog(response.data);
      } catch (error) {
        console.error('Error fetching blog:', error);
        setError('Article non trouvé');
      } finally {
        setLoading(false);
      }
    };
    fetchBlog();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Schema.org Article
  const articleSchema = blog ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": blog.title,
    "description": blog.excerpt,
    "image": blog.image_url,
    "datePublished": blog.created_at,
    "author": {
      "@type": "Organization",
      "name": "G Division"
    },
    "publisher": {
      "@type": "Organization",
      "name": "G Division",
      "url": "https://divisionimmobiliere.com"
    }
  } : null;

  const renderContent = (content) => {
    // Check if content contains HTML tags
    if (content.includes('<h2>') || content.includes('<p>') || content.includes('<ul>')) {
      return (
        <div 
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    }
    
    // Fallback: Simple markdown-like parsing for older content
    const lines = content.split('\n');
    return lines.map((line, index) => {
      if (line.startsWith('## ')) {
        return <h2 key={index} className="font-['Playfair_Display'] text-2xl font-bold text-[#1e3a5f] mt-8 mb-4">{line.replace('## ', '')}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={index} className="font-['Playfair_Display'] text-xl font-semibold text-[#c9a227] mt-6 mb-3">{line.replace('### ', '')}</h3>;
      }
      if (line.startsWith('- ')) {
        return <li key={index} className="text-gray-600 ml-4 mb-2">{line.replace('- ', '')}</li>;
      }
      if (line.match(/^\d+\./)) {
        return <li key={index} className="text-gray-600 ml-4 mb-2">{line}</li>;
      }
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={index} className="text-[#1e3a5f] font-semibold my-4">{line.replace(/\*\*/g, '')}</p>;
      }
      if (line.trim() === '') {
        return <br key={index} />;
      }
      return <p key={index} className="text-gray-600 leading-relaxed mb-4">{line}</p>;
    });
  };

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-[#1e3a5f] border-t-transparent rounded-full loading-spinner"></div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="pt-20 min-h-screen" data-testid="blog-not-found">
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-[#1e3a5f] mb-4">
            Article Non Trouvé
          </h1>
          <p className="text-gray-600 mb-8">
            L'article que vous recherchez n'existe pas ou a été supprimé.
          </p>
          <Link to="/blog">
            <Button className="bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none">
              <ArrowLeft className="mr-2" size={18} />
              Retour au Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      {blog && (
        <Helmet>
          <title>{blog.title} | G Division</title>
          <meta name="description" content={blog.excerpt} />
          <meta name="keywords" content={`${blog.category}, division immobilière, G Division`} />
          <link rel="canonical" href={`https://divisionimmobiliere.com/blog/${slug}`} />
          
          {/* Open Graph */}
          <meta property="og:title" content={blog.title} />
          <meta property="og:description" content={blog.excerpt} />
          <meta property="og:url" content={`https://divisionimmobiliere.com/blog/${slug}`} />
          <meta property="og:type" content="article" />
          <meta property="og:image" content={blog.image_url} />
          <meta property="og:site_name" content="G Division" />
          <meta property="article:published_time" content={blog.created_at} />
          
          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={blog.title} />
          <meta name="twitter:description" content={blog.excerpt} />
          <meta name="twitter:image" content={blog.image_url} />
        </Helmet>
      )}

      {/* Schema.org Article */}
      {blog && articleSchema && (
        <script 
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
        />
      )}

      <div className="pt-20" data-testid="blog-post-page">
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${blog.image_url || 'https://images.unsplash.com/photo-1657129208507-7005dfc5b36a?w=1920'})` 
          }}
        >
          <div className="absolute inset-0 hero-overlay"></div>
        </div>
        
        <div className="relative h-full flex items-end">
          <div className="max-w-4xl mx-auto px-4 md:px-8 pb-12 w-full">
            <Link 
              to="/blog" 
              className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="mr-2" size={18} />
              Retour au Blog
            </Link>
            <span className="block text-[#c9a227] uppercase tracking-wider text-sm font-semibold mb-3">
              {blog.category}
            </span>
            <h1 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {blog.title}
            </h1>
            <div className="flex items-center gap-4 text-gray-300 text-sm">
              <span className="flex items-center gap-2">
                <Calendar size={16} />
                {formatDate(blog.created_at)}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="prose max-w-none">
            {renderContent(blog.content)}
          </div>

          {/* Share */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-gray-500 text-sm">Partager cet article :</span>
              <div className="flex items-center gap-4">
                <a 
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(blog.title)}&url=${window.location.href}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#f8f9fa] rounded-full flex items-center justify-center hover:bg-[#1e3a5f] hover:text-white transition-colors text-[#1e3a5f]"
                >
                  <Share2 size={18} />
                </a>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 bg-[#f8f9fa] p-8 text-center">
            <h3 className="font-['Playfair_Display'] text-2xl font-bold text-[#1e3a5f] mb-4">
              Un Projet de Division ?
            </h3>
            <p className="text-gray-600 mb-6">
              Contactez-nous pour une consultation gratuite.
            </p>
            <Link to="/contact">
              <Button className="bg-[#1e3a5f] text-white hover:bg-[#162a45] rounded-none px-8 py-3">
                Nous Contacter
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default BlogPost;
